import {
    createContactSource,
    getContactByObjectId,
    patchContactByObjectId,
} from '@/modules/Marketing/services'
import useAuthStore from '@/stores/useAuthStore'
import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useLivechatSocket } from '../../hooks/useLivechatSocket'
import { uploadFile } from '../../services'
import {
    ChatMessage,
    ChatRoom,
    ChatRoomStatus,
    MessageType,
} from '../../types/response'
import ChatHeader from './ChatHeader'
import ChatInput, { ChatInputHandle } from './ChatInput'
import MessageList from './MessageList'

// Helper to detect file type from MIME type
const getMessageTypeFromFile = (file: File): MessageType => {
    const mimeType = file.type.toLowerCase()
    if (mimeType.startsWith('image/')) return 'image'
    if (mimeType.startsWith('audio/')) return 'audio'
    if (mimeType.startsWith('video/')) return 'video'
    return 'file'
}

interface ChatPanelProps {
    room: ChatRoom
    initialMessages: ChatMessage[]
    hasMore?: boolean
    isLoadingMore?: boolean
    onLoadMore?: () => void
    onRoomStatusChange?: (status: ChatRoomStatus) => void
    onSyncToMarketing?: () => void
    onOpenBlockModal?: () => void
    onMarkChatDoneReady?: (
        fn: (roomId: string) => Promise<{ success: boolean }>
    ) => void
}

const ChatPanel: React.FC<ChatPanelProps> = ({
    room,
    initialMessages,
    hasMore = false,
    isLoadingMore = false,
    onLoadMore,
    onRoomStatusChange,
    onOpenBlockModal,
    onMarkChatDoneReady,
}) => {
    const { t } = useTranslation()
    const { employee } = useAuthStore()

    // State
    const [messages, setMessages] = useState<ChatMessage[]>(initialMessages)
    const [inputValue, setInputValue] = useState('')
    const [isSending, setIsSending] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [typingUsers, setTypingUsers] = useState<Map<string, string>>(
        new Map()
    )

    // Refs
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const messagesContainerRef = useRef<HTMLDivElement>(null)
    const chatInputRef = useRef<ChatInputHandle>(null)
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const prevScrollHeightRef = useRef<number>(0)
    const isInitialLoadRef = useRef(true)

    // Socket event handlers
    const handleNewMessage = useCallback((message: ChatMessage) => {
        setMessages((prev) => [...prev, message])
        setTypingUsers((prev) => {
            const updated = new Map(prev)
            updated.delete(message.userId || '')
            return updated
        })
    }, [])

    const handleTyping = useCallback(
        (data: { userId: string; userName: string }) => {
            if (data.userId === employee?.employee_id?.toString()) return
            setTypingUsers((prev) =>
                new Map(prev).set(data.userId, data.userName)
            )
        },
        [employee]
    )

    const handleStopTyping = useCallback((data: { userId: string }) => {
        setTypingUsers((prev) => {
            const updated = new Map(prev)
            updated.delete(data.userId)
            return updated
        })
    }, [])

    const handleChatDone = useCallback(() => {
        onRoomStatusChange?.('done')
        toast.success(t('Chat marked as completed'))
    }, [onRoomStatusChange, t])

    // Initialize socket
    const {
        isConnected,
        isConnecting,
        error,
        sendMessage,
        sendTyping,
        sendStopTyping,
        markChatDone,
        blockUser,
    } = useLivechatSocket({
        roomId: room.id,
        onNewMessage: handleNewMessage,
        onTyping: handleTyping,
        onStopTyping: handleStopTyping,
        onChatDone: handleChatDone,
    })

    // Expose markChatDone to parent (Detail) for modal usage
    useEffect(() => {
        onMarkChatDoneReady?.(markChatDone)
    }, [markChatDone, onMarkChatDoneReady])

    // Scroll handling
    useEffect(() => {
        const container = messagesContainerRef.current
        if (!container || messages.length === 0) return

        if (prevScrollHeightRef.current > 0) {
            const newScrollHeight = container.scrollHeight
            const scrollDiff = newScrollHeight - prevScrollHeightRef.current
            container.scrollTop = scrollDiff
            prevScrollHeightRef.current = 0
        } else if (isInitialLoadRef.current) {
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
                isInitialLoadRef.current = false
            }, 100)
        } else {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
        }
    }, [messages])

    // Sync messages with props
    useEffect(() => {
        setMessages(initialMessages)
    }, [initialMessages])

    // Load more handler
    const handleLoadMore = () => {
        if (!onLoadMore || isLoadingMore) return
        const container = messagesContainerRef.current
        if (container) {
            prevScrollHeightRef.current = container.scrollHeight
        }
        onLoadMore()
    }

    // Input change handler with typing indicator
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputValue(e.target.value)

        if (e.target.value && isConnected) {
            sendTyping(room.id)

            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current)
            }

            typingTimeoutRef.current = setTimeout(() => {
                sendStopTyping(room.id)
            }, 2000)
        }
    }

    // Send message handler
    const handleSendMessage = async () => {
        if (!inputValue.trim() || isSending || !isConnected) return

        const content = inputValue.trim()
        setInputValue('')
        setIsSending(true)

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current)
        }
        sendStopTyping(room.id)

        try {
            const response = await sendMessage({
                roomId: room.id,
                content,
                type: 'text',
            })

            if (response.data) {
                setMessages((prev) => [...prev, response.data])
            }
        } catch (err: any) {
            toast.error(err.message || t('Failed to send message'))
            setInputValue(content)
        } finally {
            setIsSending(false)
            chatInputRef.current?.focus()
        }
    }

    // Key press handler
    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSendMessage()
        }
    }

    /**
     * Sync or patch marketing contact after done/block.
     * - If contact exists → patch status only
     * - If not → create new with appropriate status + assign current employee
     */
    const syncMarketingContact = async (
        status: 'warm-lead' | 'un-qualified-lead'
    ) => {
        const existing = await getContactByObjectId(room.id)
        if (existing) {
            await patchContactByObjectId(room.id, {
                contact_source_status: status,
            })
        } else {
            const customer = room.users?.find((u) => u.userType === 'user')
            const hasRequiredFields =
                customer?.name && customer?.email && customer?.phoneNumber
            const effectiveStatus =
                status === 'warm-lead' && !hasRequiredFields
                    ? 'UNCLASSIFIED'
                    : status
            await createContactSource({
                contact_source_name: customer?.name || '',
                contact_source_email: customer?.email || '',
                contact_source_phone: customer?.phoneNumber || '',
                contact_source_position: '',
                contact_source_channel: 'livechat',
                contact_source_company: '',
                contact_source_profile: '',
                contact_source_website: room.sourceOrigin || '',
                contact_source_notes:
                    status === 'un-qualified-lead'
                        ? 'Blocked from livechat'
                        : '',
                contact_source_status: effectiveStatus,
                contact_source_industry: '',
                contact_source_interested_services: [],
                contact_source_object_id: room.id,
                contact_source_object_type: 'Internal-Livechat',
                contact_source_referrer_id: null,
                contact_source_referrer_type: null,
                contact_source_assignee_ids: employee?.employee_id
                    ? [employee.employee_id]
                    : null,
                website_referrer: room.sourceUrl || '',
                company_size: '',
                deployment_time: '',
                specific_needs: '',
                customer_lead_type: 'internal_livechat',
            })
        }
    }

    // Mark as done handler
    const handleMarkDone = async () => {
        try {
            await markChatDone(room.id)
        } catch (err: any) {
            toast.error(err.message || t('Failed to mark chat as done'))
            return
        }

        onRoomStatusChange?.('done')

        try {
            await syncMarketingContact('warm-lead')
            toast.success(t('Chat marked as completed'))
        } catch (err: any) {
            console.error('Failed to sync marketing contact:', err)
            toast.error(
                err?.response?.data?.message ||
                    err?.message ||
                    t('Chat completed but failed to sync marketing contact')
            )
        }
    }

    // Block user handler — block via socket, then open sync modal for classification
    const handleBlockUser = async () => {
        const customer = room.users?.find((u) => u.userType === 'user')
        if (!customer) return

        if (!confirm(t('Are you sure you want to block this user?'))) return

        try {
            await blockUser(customer.id, room.id)
        } catch (err: any) {
            toast.error(err.message || t('Failed to block user'))
            return
        }

        // Open block + sync modal (status pre-filled as un-qualified-lead)
        onOpenBlockModal?.()
    }

    // File upload handler
    const handleFileSelect = async (
        e: React.ChangeEvent<HTMLInputElement>,
        isImage: boolean
    ) => {
        const file = e.target.files?.[0]
        if (!file) return

        e.target.value = ''

        if (!isConnected) {
            toast.error(t('Not connected. Please wait or refresh the page.'))
            return
        }

        setIsUploading(true)

        try {
            const result = await uploadFile({
                file,
                prefix: `chat_${room.id}/`,
            })

            const messageType = getMessageTypeFromFile(file)
            const response = await sendMessage({
                roomId: room.id,
                content: file.name,
                type: messageType,
                fileUrl: result.url,
                fileSize: result.fileSize,
                fileName: result.fileName,
            })

            if (response.data) {
                setMessages((prev) => [...prev, response.data])
            }

            toast.success(
                isImage
                    ? t('Image sent successfully')
                    : t('File sent successfully')
            )
        } catch (err: any) {
            console.error('File upload error:', err)
            toast.error(err.message || t('Failed to upload file'))
        } finally {
            setIsUploading(false)
            chatInputRef.current?.focus()
        }
    }

    // Check if message is from current user
    const isOwnMessage = (message: ChatMessage): boolean => {
        return (
            message.sender === 'employee' &&
            (message.user?.email === employee?.employee_email_company ||
                message.user?.keycloakUserId === employee?.employee_uuid)
        )
    }

    const isDone = room.status === 'done'
    const typingUserNames = Array.from(typingUsers.values())

    return (
        <div className="h-full flex flex-col">
            <ChatHeader
                isDone={isDone}
                isConnected={isConnected}
                isConnecting={isConnecting}
                hasError={!!error}
                onMarkDone={handleMarkDone}
                onBlockUser={handleBlockUser}
            />

            <MessageList
                ref={messagesContainerRef}
                messages={messages}
                typingUserNames={typingUserNames}
                hasMore={hasMore}
                isLoadingMore={isLoadingMore}
                onLoadMore={handleLoadMore}
                isOwnMessage={isOwnMessage}
                scrollAnchorRef={messagesEndRef}
            />

            <ChatInput
                ref={chatInputRef}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                onSend={handleSendMessage}
                onImageSelect={(e) => handleFileSelect(e, true)}
                onFileSelect={(e) => handleFileSelect(e, false)}
                isConnected={isConnected}
                isSending={isSending}
                isUploading={isUploading}
                isDone={isDone}
            />
        </div>
    )
}

export default ChatPanel
